// Ticket #212 — seed/merge `notes/orientation.md` từ template của pack.
//
// Vấn đề gốc: file này `@`-auto-load MỌI phiên Claude Code nhưng trước #212 không repo nào
// track, không code nào sinh ⇒ bản vá chỉ sống trên máy người viết. Nguồn sự thật giờ là
// `templates/orientation.md.tpl` trong pack (bất biến "CLI không chứa tri thức").
//
// HAI export tách bạch có chủ đích — KHÔNG gộp:
//   - ensureOrientationNoteFromPack     → chỉ seed/merge `notes/orientation.md`
//   - appendOrientationImportToClaudeMd → chỉ append dòng `@notes/orientation.md`
// Lý do: ở `sync.ts` hai việc chạy ở HAI vị trí cách nhau ~110 dòng. Append phải nằm SAU
// `mergeRootClaudeMdManaged` vì `CLAUDE.md` không marker rơi nhánh "kept-below-end" = ghi
// lại toàn file (#254: block mới trên, nội dung cũ dưới END) ⇒ append trước merge thì dòng
// import lẫn vào nội dung cũ thay vì nằm cuối vùng user.
//
// TÍNH IDEMPOTENT nằm ở "nội dung không đổi ⇒ KHÔNG ghi" — không phải ở việc render lại
// chuỗi giống hệt. Ghi lại mỗi sync sẽ đổi mtime và đánh thức mọi thứ theo dõi file.
//
// KHÔNG LOCK (tiền lệ #172): đây không phải `settings.json`; đua tệ nhất = mất 1 lần append,
// sync kế tiếp tự lành.

import { lstat, readFile } from "node:fs/promises";
import { join } from "node:path";
import { ensureDir, writeTextAtomic } from "./filesystem-helpers.js";
import {
  MANAGED_END_PREFIX,
  MANAGED_START_PREFIX,
  mergeClaudeMdManagedBlock,
} from "./merge-claude-md-managed-block.js";
import { renderTemplate } from "./mustache-template-engine.js";
import { backupIfExists } from "./project-tree-scaffolder.js";

// ⚠️ Path literal là LOAD-BEARING cho gate build của phase-02 (đếm chuỗi "orientation" trong
// bundle đã build ≥ 1): tên hàm bị minify, chỉ chuỗi này sống sót.
const TEMPLATE_RELATIVE = [".claude", "pack", "templates", "orientation.md.tpl"] as const;
const NOTE_RELATIVE = ["notes", "orientation.md"] as const;

export const ORIENTATION_IMPORT_LINE = "@notes/orientation.md";
// Sentinel opt-out (quyết định D2). Người dùng tự thêm vào vùng user của CLAUDE.md;
// CLI KHÔNG BAO GIỜ tự ghi chuỗi này. Thấy sentinel ⇒ không append (vẫn seed file).
export const ORIENTATION_OPT_OUT_SENTINEL = "<!-- avatar:orientation-import: off -->";
const IMPORT_COMMENT = "<!-- Avatar (#212): bản đồ workspace, seed từ pack qua 'avatar sync'. -->";

export type OrientationStatus =
  | "created"
  | "replaced-block"
  | "no-op"
  | "no-op-pack-missing"
  | "backed-up-replaced"
  | "skipped-symlink"
  | "failed";

// Chỉ append import khi file đích THỰC SỰ có mặt sau thao tác. `no-op` và
// `backed-up-replaced` PHẢI nằm trong tập này: máy đã có orientation viết tay chính là
// nhóm gây ra #212 — chặn ở "created" thì họ không bao giờ có `@import`.
const STATUS_CHO_PHEP_APPEND: ReadonlySet<OrientationStatus> = new Set<OrientationStatus>([
  "created",
  "replaced-block",
  "no-op",
  "backed-up-replaced",
]);

export interface EnsureOrientationResult {
  status: OrientationStatus;
  backupPath?: string;
  warning?: string;
}

export interface AppendOrientationImportResult {
  appended: boolean;
  warning?: string;
}

// writeTextAtomic là tmp+rename, mà rename KHÔNG đi theo symlink: nó THAY THẾ symlink bằng
// file thường. Ai trỏ `notes/` (hoặc file đích) sang chỗ khác sẽ đứt liên kết ÂM THẦM.
async function laSymlink(path: string): Promise<boolean> {
  try {
    return (await lstat(path)).isSymbolicLink();
  } catch (err) {
    // ENOENT = không có gì ở đó (kể cả symlink) ⇒ ghi thoải mái. Lỗi khác để catch ngoài xử.
    if ((err as NodeJS.ErrnoException)?.code !== "ENOENT") throw err;
    return false;
  }
}

async function docNeuCo(path: string): Promise<string | null> {
  try {
    return await readFile(path, "utf8");
  } catch (err) {
    // ENOENT = file vắng (ca hợp lệ). Lỗi khác (EACCES, EISDIR) KHÔNG được coi là vắng —
    // ghi đè lên thứ ta không đọc được là hành vi phá hoại.
    if ((err as NodeJS.ErrnoException)?.code !== "ENOENT") throw err;
    return null;
  }
}

function coDuCapMarker(content: string): boolean {
  const lines = content.split("\n");
  const s = lines.findIndex((l) => l.trimStart().startsWith(MANAGED_START_PREFIX));
  const e = lines.findIndex((l) => l.trimStart().startsWith(MANAGED_END_PREFIX));
  return s !== -1 && e !== -1 && s < e;
}

/**
 * Seed hoặc merge `notes/orientation.md` từ template của pack. KHÔNG đụng `CLAUDE.md`.
 *
 * @param projectRoot workspace root (đường tuyệt đối) — cũng là giá trị biến `workspaceRoot`
 */
export async function ensureOrientationNoteFromPack(
  projectRoot: string,
): Promise<EnsureOrientationResult> {
  const templatePath = join(projectRoot, ...TEMPLATE_RELATIVE);
  const notesDir = join(projectRoot, NOTE_RELATIVE[0]);
  const dest = join(projectRoot, ...NOTE_RELATIVE);
  try {
    const template = await docNeuCo(templatePath);
    // Pack cũ (chưa mang template) ⇒ no-op IM LẶNG. CLI mới phải chịu được pack cũ.
    if (template === null) return { status: "no-op-pack-missing" };

    if ((await laSymlink(notesDir)) || (await laSymlink(dest))) {
      return {
        status: "skipped-symlink",
        warning: `${dest} (hoặc thư mục notes/) là symlink — không ghi (ghi nguyên tử sẽ thay symlink bằng file thường). Sửa tay file đích rồi chạy lại.`,
      };
    }

    // Đúng 1 biến. renderTemplate GIỮ NGUYÊN biến không khớp key
    // (`mustache-template-engine.ts:16`) ⇒ sai tên key thì file vẫn ra đời, chỉ mang
    // literal `{{workspaceRoot}}` — nghĩa vụ này có ca test + AC grep riêng.
    const rendered = renderTemplate(template, { workspaceRoot: projectRoot });

    const current = await docNeuCo(dest);
    if (current === null || current.trim() === "") {
      await ensureDir(notesDir);
      await writeTextAtomic(dest, rendered);
      return { status: "created" };
    }

    // File không marker = bản viết tay của người dùng ⇒ BẮT BUỘC backup trước khi ghi đè.
    // Đây là rủi ro nặng nhất của cả plan: nhánh no-marker của hàm merge là "thay hoàn toàn".
    if (!coDuCapMarker(current)) {
      const backupPath = await backupIfExists(dest);
      await writeTextAtomic(dest, rendered);
      return { status: "backed-up-replaced", backupPath: backupPath ?? undefined };
    }

    const merged = mergeClaudeMdManagedBlock(current, rendered);
    if (merged.content === current) return { status: "no-op" };
    await writeTextAtomic(dest, merged.content);
    return { status: "replaced-block" };
  } catch (err) {
    return {
      status: "failed",
      warning: `không cập nhật được ${dest}: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
}

/**
 * Append dòng `@notes/orientation.md` vào VÙNG USER của `CLAUDE.md` (cuối file, dưới
 * `AVATAR:MANAGED:END` — quyết định c).
 *
 * ⚠️ Vị trí cuối file chỉ an toàn khi gọi SAU `mergeRootClaudeMdManaged` trong `sync.ts`:
 * file không marker bị merge trả "kept-below-end" = ghi lại toàn file (block mới trên, nội dung cũ dưới).
 */
export async function appendOrientationImportToClaudeMd(
  projectRoot: string,
  status: OrientationStatus,
): Promise<AppendOrientationImportResult> {
  if (!STATUS_CHO_PHEP_APPEND.has(status)) return { appended: false };
  const claudeMdPath = join(projectRoot, "CLAUDE.md");
  try {
    const current = await docNeuCo(claudeMdPath);
    // Chưa có CLAUDE.md ⇒ không tự tạo: file đó là việc của `avatar init`.
    if (current === null) return { appended: false };
    // Opt-out (D2): người dùng đã chủ động tắt auto-load ⇒ tôn trọng, không append lại.
    if (current.includes(ORIENTATION_OPT_OUT_SENTINEL)) return { appended: false };
    if (current.includes(ORIENTATION_IMPORT_LINE)) return { appended: false };
    if (await laSymlink(claudeMdPath)) {
      return {
        appended: false,
        warning: `${claudeMdPath} là symlink — không ghi (ghi nguyên tử sẽ thay symlink bằng file thường).`,
      };
    }

    // trimEnd() xử ca file KHÔNG có newline cuối: thiếu bước này thì comment bị DÁN vào
    // dòng cuối của user.
    const next = `${current.trimEnd()}\n\n${IMPORT_COMMENT}\n${ORIENTATION_IMPORT_LINE}\n`;
    await writeTextAtomic(claudeMdPath, next);
    return { appended: true };
  } catch (err) {
    return {
      appended: false,
      warning: `không thêm được '${ORIENTATION_IMPORT_LINE}' vào ${claudeMdPath}: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
}
